/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import * as Service from 'types/service';
import { DisplayBookingFee } from 'components/service/party-panel/DisplayBookingFee';
import { DisplayTime } from '@tock/time';
import { GuestCRMAttributes, GuestCRMIntegration } from 'types';
import { IWithContextProps, withContext } from '@tock/coretypes';
import { IconCheckmarkCircle } from '@tock/icons';
import { Typography } from 'bento';
import { createOptionMap, visitGeneralNotes } from 'utils/purchaseUtils';
import {
  deliveryServiceProviderDisplayName,
  isThirdPartyDeliveryVariety,
  isTicketTypeDiningVariety,
} from '@tock/shared/utils/offerings';
import {
  getFullName,
  hasBusinessProfiles,
  isPartyOnlineWalkIn,
  textMessageHasStatusIcon,
} from 'utils/partyUtils';
import { getGuestIntegrationInformation } from 'utils/guestIntegrationsUtils';
import { getHasAnsweredQuestions } from '@tock/shared/utils/questionUtils';
import { ifClickable } from '@tock/shared/utils/touchUtils';
import { oc } from 'ts-optchain';
import { shouldHideAllSeatingAreasSelector } from 'selectors/service/shouldHideAllSeatingAreasSelector';
import FeatureFlag from '@tock/shared/utils/featureFlagContext';
import GuestAndVisitIcons from 'components/crm/GuestAndVisitIcons';
import PartyDuration from 'components/service/party/PartyDuration';
import _ from 'lodash';
import clsx from 'clsx';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface PartyBodyCondensedProps {
  className?: string;
  isHD?: boolean;
  isSelected: boolean;
  onClick?(event: React.MouseEvent<HTMLElement>): void;
  party: Service.Party;
  showGuestListNotes?: boolean;
  showGuestListTags?: boolean;
  isEligibleForHoldCharge?: boolean;
}

interface ConnectProps extends DefaultConnectProps {
  shouldHideAllSeatingAreas: boolean;
}

type Props = PartyBodyCondensedProps & ConnectProps & IWithContextProps;

class PartyBodyCondensed extends React.PureComponent<Props> {
  private MAX_ICONS_TO_DISPLAY = 3;

  getPurchasedOptions(): boolean {
    const { party } = this.props;

    const purchase = oc(party).purchase();

    if (purchase) {
      return !_.isEmpty(createOptionMap(purchase.purchasedOption));
    }

    return false;
  }

  getGuestIntegration(type: GuestCRMIntegration): GuestCRMAttributes {
    const { party } = this.props;
    const attributes = hasBusinessProfiles(party)
      ? oc(party.primaryDinerProfile).attribute([])
      : [];
    return getGuestIntegrationInformation(type, attributes);
  }

  render() {
    const {
      className,
      isHD,
      isSelected,
      party,
      onClick,
      showGuestListNotes,
      showGuestListTags,
      context: { isFeatureEnabled },
      shouldHideAllSeatingAreas,
      isEligibleForHoldCharge,
    } = this.props;
    const cssClass = clsx('PartyBodyCondensed', 'seating-area-enabled', className, {
      'is-selected': isSelected,
    });

    const visit = oc(party).visit({} as any);
    const ticketType = party.purchase?.ticketType;
    const visitTags = oc(visit).tag([]);
    const guestProfile = hasBusinessProfiles(party) ? oc(party).primaryDinerProfile() : undefined;
    const quickTags = oc(party).tag([]);

    const hasPaymentRequired = party.requiresPayment;
    const hasNewMessage = party.hasUnreadTextMessage;
    const lastTextMessageStatus = oc(
      _.last(party.textMessage.filter(textMessageHasStatusIcon))
    ).status();
    const hasQuickNote = !_.isEmpty(party.shortNotes);
    const hasVisitNote = _.some(oc(party).purchase.auditedBookingNote(), (auditedNote: any) => {
      return !_.isEmpty(auditedNote.note) && auditedNote.noteType !== 'DIETARY';
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const visitNote = oc(visitGeneralNotes(oc(party).purchase()!)).note();
    const hasBeenConfirmed = oc(party).purchase.confirmedViaSms();
    const hasVisitDietaryNote = _.some(oc(party).purchase.auditedBookingNote(), (auditedNote) => {
      return !_.isEmpty(auditedNote.note) && auditedNote.noteType === 'DIETARY';
    });
    const hasAnsweredQuestion = getHasAnsweredQuestions(party?.purchase?.question ?? []);
    const hasPurchasedSupplements = this.getPurchasedOptions();
    const experienceTip = _.size(party.experience) > 25 ? party.experience : '';
    const experienceShortCode = oc(party).purchase.ticketType.shortCode('');
    const hasDelivered = !!party.purchase?.visit?.delivered;
    const hasDeliverySignatureRequirement = party.purchase?.hasDeliverySignatureRequirement;

    let totalVisitsCount = 0;
    if (
      party.type === 'Booking' ||
      (party.type === 'WalkIn' && party.walkInType === 'Associated')
    ) {
      totalVisitsCount = party?.currentBusinessGuestProfile?.pastVisits ?? 0;
    }
    const isDelivery = party?.purchase?.ticketType?.variety === 'ORDER_DELIVERY';
    const isPickup = party?.purchase?.ticketType?.variety === 'ORDER_PICKUP';

    let description = party.type === 'WalkIn' ? party.experience : experienceShortCode;
    if (isPartyOnlineWalkIn(party)) {
      description += ' (online)';
    }

    const hasSeatingArea = !_.isEmpty(party.purchase?.seatingAreaName);
    const showSeatingAreaText =
      hasSeatingArea &&
      !shouldHideAllSeatingAreas &&
      isTicketTypeDiningVariety(party?.purchase?.ticketType);

    let name = party.lastNameFirstName;
    if (isFeatureEnabled('FIX_TIMELINE_ZOMBIE_NAME')) {
      if (party?.purchase?.dinerPatron?.[0]) {
        name = party
          ? getFullName({
              firstName: party.purchase.dinerPatron[0].firstName || '',
              lastName: party.purchase.dinerPatron[0].lastName || '',
              email: party.purchase.dinerPatron[0].email || '',
              lastNameFirst: true,
            })
          : '';
      } else if (_.isEmpty(name)) {
        name = party
          ? getFullName({
              firstName: party.patronInfo.firstName,
              lastName: party.patronInfo.lastName,
              email: party.patronInfo.lastName,
              lastNameFirst: true,
            })
          : '';
      }
    }

    const isPartyNoShowOrCancellation =
      party.purchase?.primaryState === 'NO_SHOW' || party.purchase?.primaryState === 'CANCELED';

    return (
      <div className={cssClass} onClick={ifClickable(onClick)} data-testid="party-body">
        <div className="PartyBodyCondensed-title">
          <span className="PartyBodyCondensed-name" data-testid="party-name">
            {name}
          </span>
          <span
            className={clsx('PartyBodyCondensed-size', {
              'PartyBodyCondensed-size--large': party.isLargeParty,
            })}
          >
            {party.partySize}
          </span>
        </div>
        <div
          data-testid="party-description"
          className={clsx('PartyBodyCondensed-description', {
            'no-show-or-cancellation': isPartyNoShowOrCancellation,
          })}
        >
          <div
            className="PartyBodyCondensed-experience showMore"
            data-for="tooltip"
            data-tip={experienceTip}
          >
            <span>
              {isPartyNoShowOrCancellation ? (
                <DisplayBookingFee
                  paymentCardHold={party.purchase?.paymentCardHold}
                  isEligibleForCharge={isEligibleForHoldCharge ?? false}
                  isEligibleForRefund={party.purchase?.paymentCardHold?.status === 'HOLD_CHARGED'}
                />
              ) : (
                <>
                  {description}
                  {showSeatingAreaText && (
                    <Typography
                      component="span"
                      variant="body"
                      color="default"
                      data-testid="seating-option"
                    >
                      {' • '}
                      {party.purchase?.seatingAreaName}
                    </Typography>
                  )}
                </>
              )}
            </span>
            <FeatureFlag ifEnabled="SHOW_DELIVERY_ORDER_AT_PICKUP_TIME">
              {(showDeliveryOrderAtPickupTime) => (
                <>
                  {isDelivery &&
                    (showDeliveryOrderAtPickupTime || isThirdPartyDeliveryVariety(ticketType)) && (
                      <span>
                        {' '}
                        <DisplayTime timeValue={party.ticketDateTime} />
                      </span>
                    )}
                </>
              )}
            </FeatureFlag>
            {isDelivery &&
              isThirdPartyDeliveryVariety(ticketType) &&
              ` ${deliveryServiceProviderDisplayName(ticketType.deliveryServiceProvider)} delivery`}
            {isDelivery &&
              party.purchase?.ticketType?.deliveryServiceProvider === 'INTERNAL' &&
              ' delivery'}
            {isPickup && ' pickup'}
          </div>

          {isHD && (
            <div
              className={clsx('PartyBodyCondensed-visits', { 'is-disabled': !totalVisitsCount })}
            >
              <IconCheckmarkCircle />
              <span data-testid="party-visit-count" className="PartyBodyCondensed-visit-count">
                {totalVisitsCount}
              </span>
            </div>
          )}

          {showGuestListTags && (
            <GuestAndVisitIcons
              guestProfile={guestProfile}
              visitTags={visitTags}
              quickTags={quickTags}
              visitNote={visitNote}
              hasBeenConfirmed={hasBeenConfirmed}
              hasQuickNote={hasQuickNote}
              hasVisitNote={hasVisitNote}
              hasVisitDietaryNote={hasVisitDietaryNote}
              hasPaymentRequired={hasPaymentRequired}
              hasNewMessage={hasNewMessage}
              hasAnsweredQuestion={hasAnsweredQuestion}
              hasPurchasedSupplements={hasPurchasedSupplements}
              hasDelivered={hasDelivered}
              lastTextMessageStatus={lastTextMessageStatus}
              maxIconsToDisplay={this.MAX_ICONS_TO_DISPLAY}
              hasDeliverySignatureRequirement={hasDeliverySignatureRequirement}
            />
          )}
          {!isHD && <PartyDuration className="PartyBodyCondensed-duration" party={party} />}
        </div>

        {isHD ? (
          <>
            {showGuestListNotes && (
              <div className={clsx('PartyBodyCondensed-notes', { 'is-empty': !visitNote })}>
                {visitNote || 'Add Note...'}
              </div>
            )}
            <PartyDuration className="PartyBodyCondensed-duration is-hd" party={party} />
          </>
        ) : (
          showGuestListNotes &&
          visitNote && <div className="PartyBodyCondensed-notes">{visitNote}</div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state: StoreType) {
  return {
    shouldHideAllSeatingAreas: shouldHideAllSeatingAreasSelector(state),
  };
}

export default connect<PartyBodyCondensedProps, ConnectProps>(
  withContext(PartyBodyCondensed),
  mapStateToProps
);
